import { AssigneeSelect } from "src/base-components/AssigneeSelect";
import { CaseAssigneePill } from "src/cases/common/CaseAssigneePill";
import { ClaimCaseButton } from "src/cases/common/ClaimCaseAction";
import { useAssignCase, useIsQueueMember } from "src/cases/hooks";
import { useQueueMembers } from "src/cases/queues/hooks";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { UserNotFoundPill } from "src/entities/Assignees";
import { useCapabilities } from "src/hooks/useCapabilities";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type CaseAssigneeProps = {
  assignee?: string;
  caseId: string;
  queueId: string;
  etag: string;
};

export const CaseAssignee: React.FC<CaseAssigneeProps> = ({
  assignee,
  caseId,
  queueId,
  etag,
}) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const {
    data: members = [],
    isLoading: isLoadingMembers,
    queueMemberIds,
  } = useQueueMembers(queueId, assignee);
  const capabilities = useCapabilities();
  const { onAssign: assignCase, isPending } = useAssignCase(queueId);
  const isQueueMember = useIsQueueMember(queueId);

  const isAssigneeNonMember = !!assignee && !queueMemberIds.has(assignee);

  const onAssign = async (userId: Nullable<string>) => {
    return assignCase({
      caseId,
      etag,
      assigneeId: userId,
    });
  };

  if (isLoadingMembers) {
    return <Skeleton height="h-5.5" width="w-40" />;
  }

  if (capabilities.cases.canAssign) {
    const select = (
      <AssigneeSelect
        dataLoc="case-assignee-select"
        disabled={isPending || isLoadingMembers}
        isLoading={isLoadingMembers}
        users={members}
        value={assignee}
        variant="pill"
        workspaceId={wsId}
        onChange={onAssign}
      />
    );

    if (isAssigneeNonMember) {
      return (
        <Tooltip title="This user is no longer a queue member" asChild>
          <div>{select}</div>
        </Tooltip>
      );
    }

    return select;
  }

  if (!assignee) {
    return (
      <ClaimCaseButton
        disabled={!isQueueMember}
        loading={isPending}
        onClick={onAssign}
      />
    );
  }

  const user = members.find((user) => user.id === assignee);
  if (!user) return <UserNotFoundPill id={assignee} />;

  if (isAssigneeNonMember) {
    return (
      <Tooltip title="This user is no longer a queue member" asChild>
        <div>
          <CaseAssigneePill assignee={user} />
        </div>
      </Tooltip>
    );
  }

  return <CaseAssigneePill assignee={user} />;
};
